import { PaymentMethod } from '@stripe/stripe-js';
import { useSearchParams } from 'next/navigation';
import { useCallback } from 'react';

import type {
	PostSubscriptionsStripeRequestBody,
	PostSubscriptionsStripeResolvePlanRequestBody,
	PostSubscriptionsStripeResolvePlanResponseBody,
	PostSubscriptionsStripeResponseBody,
} from '@calm-web/api-sdk/lib/app-api';
import type {
	SubscriptionsStripeInitializeV1Errors,
	SubscriptionsStripeSubscriptionInitializationRequest,
	SubscriptionsStripeSubscriptionInitializationResponse,
} from '@calm-web/api-sdk/lib/atlas-subscriptions-api';

import { type PurchaseBodyArgs } from '@/components/purchase/PaymentElementsForm/utils';
import { useApi } from '@/hooks/api';
import { isAtlasSubscriptionsApiError } from '@/hooks/api/types';
import useIsCalmSleepApp from '@/hooks/useIsCalmSleepApp';

import useSubscriptionsAnalyticsProperties from './useSubscriptionsAnalyticsProperties';

export type UseGetClientSecretProps = {
	purchaseBodyArgs?: PurchaseBodyArgs;
	sku?: string;
};

export default function useGetClientSecret({ purchaseBodyArgs, sku }: UseGetClientSecretProps) {
	const isCalmSleepApp = useIsCalmSleepApp();
	const apiRequest = useApi();
	const searchParams = useSearchParams();
	const { getSubscriptionsAnalyticsProperties } = useSubscriptionsAnalyticsProperties();

	const getClientSecret = useCallback(
		async (paymentMethod: PaymentMethod) => {
			const { country, postal_code } = paymentMethod.billing_details.address ?? {};

			const postalCode = postal_code ?? undefined;
			const address = country
				? {
						country,
						postalCode,
					}
				: undefined;

			let clientSecret: string | undefined;
			if (isCalmSleepApp) {
				if (!sku) {
					return { error: new Error('SKU is required') };
				}
				try {
					const body: SubscriptionsStripeSubscriptionInitializationRequest = {
						offer_code: searchParams?.get('offer_code') ?? undefined,
						sku,
						analyticsProperties: getSubscriptionsAnalyticsProperties(),
						address,
					};

					({
						data: { clientSecret },
					} = await apiRequest<SubscriptionsStripeSubscriptionInitializationResponse>({
						service: 'calm-sleep-app-web-api',
						endpoint: 'stripe-client-secret',
						method: 'POST',
						body,
					}));
				} catch (err) {
					const error = new Error('We couldn’t complete your request. Please try again.');
					if (isAtlasSubscriptionsApiError<SubscriptionsStripeInitializeV1Errors[400]>(err)) {
						if (err.data?.errorCode === 'eligibility_error_already_subscribed') {
							error.message = 'You already have an active subscription.';
						} else if (err.data?.errorCode === 'stripe_invalid_tax_location') {
							error.message = 'We couldn’t verify your ZIP code. Please try again.';
						}
					}
					return { error };
				}
			} else {
				if (!address) {
					return { error: new Error('Address is required') };
				}

				if (!purchaseBodyArgs) {
					return { error: new Error('Purchase body args are required') };
				}

				try {
					const { data: legacyPlanDetails } =
						await apiRequest<PostSubscriptionsStripeResolvePlanResponseBody>({
							service: 'api',
							endpoint: 'subscriptions/stripe/resolve-plan',
							method: 'POST',
							body: purchaseBodyArgs satisfies PostSubscriptionsStripeResolvePlanRequestBody,
						});

					const body: PostSubscriptionsStripeRequestBody = {
						legacyPlanDetails,
						address,
					};

					({
						data: { clientSecret },
					} = await apiRequest<PostSubscriptionsStripeResponseBody>({
						service: 'web-api',
						endpoint: 'stripe-client-secret',
						method: 'POST',
						body,
					}));
				} catch {
					return { error: new Error('We couldn’t complete your request. Please try again.') };
				}
			}
			return { clientSecret };
		},
		[apiRequest, getSubscriptionsAnalyticsProperties, isCalmSleepApp, purchaseBodyArgs, searchParams, sku],
	);

	return getClientSecret;
}
